import { useEffect, useMemo, useState } from 'react';
import {
  ComponentRendering,
  Field,
  GetStaticComponentProps,
  LayoutServiceData,
  withDatasourceCheck,
} from '@sitecore-jss/sitecore-jss-nextjs';
import { sanitize } from 'isomorphic-dompurify';
import { isEmpty, uniq, uniqBy } from 'lodash';
import { GetStaticPropsContext } from 'next';

import {
  getTailwindColor,
  mapProductColor,
  resolveColorProperty,
} from '@gn-audio/jss-components/utils';
import { useSiteSettings } from '@gn-audio/jss-components/utils/contexts';
import { Family, getFamilyPrices, getLocaleWithRegion } from '@gn-audio/jss-core';

import { fetchColorHexMappings } from 'api/colorHexMappingApi';
import { mapToStandardPriceModel } from 'api/pricesApi/priceMapper';
import { StandardPriceModel } from 'api/pricesApi/pricesModel';
import {
  FamilyGroup,
  getAttributesByFamily,
  getAttributesByGroups,
  getFamilies,
} from 'api/productCatalogueApi';
import { getSharedDictionary } from 'common/getters/getSharedDictionary';
import { parseSCId, parseSCLookupValueList } from 'common/siteCoreHelpers';
import { ProductEntityType } from 'common/types/productTypes';
import { SharedField } from 'components/Product/Buy/EcomBenefits/EcomBenefits';
import {
  RelatedProductsItem,
  RelatedProductTile,
} from 'composables/Product/Buy/RelatedProductsItem/RelatedProductsItem';
import { ComponentProps } from 'lib/component-props';
import {
  getAttributeByKey,
  getProductBuyPageUrl,
  getProductTitledHexColorOption,
  GroupAttributeKeys,
} from 'utils/productUtils';
import { isNotNil } from 'utils/utils';

type RelatedProductsFields = {
  products: Field<string>;
  productsShared: Field<string>;
  withHeroProduct: Field<boolean>;
  title: Field<string>;
  titleShared: SharedField<string>;
  backgroundColor: Field<string>;
  cardBackgroundColorShared: Field<string>;
  filters: {
    name: string;
  }[];
};

type RelatedProductTiles = RelatedProductTile[];

type RelatedProductsProps = {
  fields: RelatedProductsFields;
  relatedProductTiles: RelatedProductTiles;
} & ComponentProps;

const gridClasses = (relatedProductsAmount: number, isHero: boolean): string => {
  const baseClasses = 'grid grid-cols-1 gap-2';
  let columnClasses = '';
  let widthClasses = '';

  // Determine column classes based on related products amount
  switch (relatedProductsAmount) {
    case 1:
      columnClasses = isHero ? '2xl:grid-cols-2' : 'w-auto lg:w-1/3';
      break;
    case 2:
      columnClasses = isHero ? 'lg:grid-cols-3 md:w-3/5' : 'lg:w-3/4 md:grid-cols-2';
      break;
    default:
      columnClasses = `md:grid-cols-2 lg:grid-cols-3 ${isHero ? '2xl:grid-cols-4' : ''}`;
  }

  return `${baseClasses} ${columnClasses} ${widthClasses}`;
};
const RelatedProducts = ({ relatedProductTiles, fields, rendering }: RelatedProductsProps) => {
  const [prices, setPrices] = useState<StandardPriceModel[] | null | undefined>();
  const { sfcc, ecom } = useSiteSettings();

  const dataSourceId = parseSCId(rendering.dataSource);
  const title = getSharedDictionary(fields, 'title')?.value;

  const familyIds = useMemo(
    () => uniq(relatedProductTiles.map(({ familyId }) => familyId)),
    [relatedProductTiles]
  );

  useEffect(() => {
    if (ecom?.ecomEnabled !== undefined) {
      const fetchData = async () => {
        if (!ecom.ecomEnabled) {
          setPrices(null);
          return;
        }
        const data = await getFamilyPrices(familyIds);
        const fetchedPrices = Array.isArray(data) ? data.map(mapToStandardPriceModel) : [];

        setPrices(fetchedPrices.length === 0 ? null : fetchedPrices);
      };

      void fetchData();
    }
  }, [familyIds, sfcc, ecom?.ecomEnabled]);

  const backgroundColor = resolveColorProperty(fields.backgroundColor?.value);
  const cardBackgroundColor = resolveColorProperty(fields.cardBackgroundColorShared?.value);

  return (
    <div>
      <section
        id={`related-products-${dataSourceId}`}
        aria-labelledby={`section-title-${dataSourceId}`}
        style={{ backgroundColor }}
      >
        <div className="container">
          {title && (
            <header>
              <h2
                id={`section-title-${dataSourceId}`}
                className="heading-2 mb-12 text-center md:mb-24"
              >
                {title}
              </h2>
            </header>
          )}
          <div className="flex items-center justify-center">
            <div className={gridClasses(relatedProductTiles.length, fields.withHeroProduct.value)}>
              {relatedProductTiles.map((productTile, index) => {
                return (
                  <RelatedProductsItem
                    key={`related-product-item-${productTile.familyId}`}
                    productTile={productTile}
                    prices={prices}
                    heroItem={index === 0 && fields.withHeroProduct.value}
                    cardBackgroundColor={cardBackgroundColor}
                  />
                );
              })}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

const getProductName = (groups: FamilyGroup[], family: Family): string => {
  return (groups.length === 1 ? groups[0].productName : family.productName) || '';
};

const getProductPageUrl = (groups: FamilyGroup[], family: Family): string | undefined => {
  return groups.length === 1 ? groups[0].pageUrl : family.pageUrl;
};

const DEFAULT_PRODUCT_BRAND_COLOR = 'beige';

export const getStaticProps: GetStaticComponentProps = async (
  rendering: ComponentRendering,
  _: LayoutServiceData,
  context: GetStaticPropsContext
) => {
  try {
    const dataSource = rendering.fields as unknown as RelatedProductsFields;
    const language = getLocaleWithRegion(context.locale);

    const lookupProductsList = parseSCLookupValueList(
      dataSource?.products.value || dataSource?.productsShared.value
    );
    const { familyIds, groupIds } = lookupProductsList.reduce(
      (acc, product) => ({
        ...acc,
        groupIds:
          product.type === ProductEntityType.Group ? [...acc.groupIds, product.id] : acc.groupIds,
        familyIds:
          product.type === ProductEntityType.Family
            ? [...acc.familyIds, product.id]
            : acc.familyIds,
      }),
      {
        groupIds: [],
        familyIds: [],
      }
    );

    const familiesAttributesList = await Promise.all(
      familyIds.map((id) => getAttributesByFamily(id, language))
    );
    const groupAttributesList = groupIds.length
      ? await getAttributesByGroups(groupIds, language)
      : [];
    const allFamilyIds = [
      ...new Set([...familyIds, ...groupAttributesList.map((group) => group.familyId)]),
    ];

    const relatedProductsFamilies = await getFamilies(allFamilyIds, language);

    const colorHexMappings = await fetchColorHexMappings(context.locale);

    const relatedProductItems = lookupProductsList
      .map((product) => {
        if (product.type === ProductEntityType.Family) {
          const groups =
            familiesAttributesList
              .filter((list) => !isEmpty(list))
              .find((familyGroups) => familyGroups[0].familyId === Number(product.id)) || [];
          return isEmpty(groups) ? null : groups;
        }

        const group = groupAttributesList.find((group) => group.groupId === Number(product.id));
        return group ? [group] : null;
      })
      .filter(isNotNil);

    let relatedProductTiles = [];
    for (let i = 0; i < relatedProductItems.length; i++) {
      const relatedGroup = relatedProductItems[i];
      const relatedFamily = relatedProductsFamilies.find(
        (family) => family.familyId === relatedGroup[0].familyId
      ) as Family;
      const uniqueByColors = uniqBy(
        relatedGroup,
        (group) => getAttributeByKey(group.attributes, GroupAttributeKeys.COLOR_HEX)?.value
      );

      const lifestyleImageAttribute =
        uniqueByColors[0]?.attributes.find(
          (attr) => attr.key === GroupAttributeKeys.LIFESTYLE_IMAGE
        ) ?? null;

      const variants = uniqueByColors.map((group) => ({
        image: group?.images[0]?.url ?? null,
        productBuyPageUrl: getProductBuyPageUrl(group.attributes) ?? null,
        color: getProductTitledHexColorOption(group.attributes, colorHexMappings) ?? null,
        bgColor: getTailwindColor(
          mapProductColor(
            getAttributeByKey(group.attributes, 'Product_BrandColorPublicWeb')?.value ||
              DEFAULT_PRODUCT_BRAND_COLOR
          )
        ),
        productId: group.productId,
      }));
      const uniqueSellingPoint = sanitize(relatedFamily.uniqueSellingPoint || '');

      const productName = getProductName(relatedGroup, relatedFamily);
      const productPageUrl = getProductPageUrl(relatedGroup, relatedFamily);
      relatedProductTiles[i] = {
        uniqueSellingPoint,
        lifestyleImageAttribute,
        variants,
        productName,
        productPageUrl,
        familyId: relatedFamily.familyId,
        colorOptions: variants.map((group) => group.color).filter(isNotNil),
      };
    }

    return {
      fields: dataSource,
      relatedProductTiles,
    };
  } catch (e) {
    console.log(e);
    return {};
  }
};

export default withDatasourceCheck()<RelatedProductsProps>(RelatedProducts);
